import { cn } from '../../../lib/utils.ts'
import { Heading } from '../heading/heading.tsx'
import { Sidebar } from '../sidebar/sidebar.tsx'

/**
 * `View` is the screen frame: `View.Root` + `View.Header` (with `View.Title`) + `View.Actions`
 * + `View.Body` + `View.Footer`. No data, no domain -- its one app-level tie is `View.Header`, which
 * carries the navigation trigger when a `Sidebar.Provider` is mounted above it, and nothing otherwise.
 * Compose a screen by assembling these slots; use `View.Title` for the page heading so every screen
 * gets the same responsive title sizing for free (drop a raw `Heading` only for a deliberate
 * one-off). The matching pending skeleton (`patterns/view-skeleton`) reuses the same frame so the
 * crossfade has no layout shift.
 *
 * `View.Root` caps the reading column at 800px of content and centers it in the routed region, so
 * every screen shares one content width instead of stretching to the viewport. The cap includes the
 * page gutter the slots add, so a wide window gives 800px of content (the gutter is absorbed by the
 * centering) and a narrow one keeps the gutter as breathing room. No breakpoint involved: this holds
 * whatever the nav column takes.
 *
 * @summary Composable view frame (header / title / actions / body / footer).
 */
function ViewRoot(props: ViewRoot.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="view-root"
      className={cn('mx-auto flex min-h-full w-full max-w-[calc(800px_+_2*var(--spacing-page))] flex-col', className)}
      {...rest}
    />
  )
}
namespace ViewRoot {
  export type Props = React.ComponentProps<'div'>
}

function ViewHeader(props: ViewHeader.Props) {
  const { className, children, ...rest } = props
  return (
    <div
      data-slot="view-header"
      // No bottom padding: the gap down to the body is the body's own top gutter.
      // Sticky on mobile only: the bar there has a background and an elevation, which is what lets
      // content pass under it. On desktop the header is transparent, so it scrolls with the page.
      className={cn(
        'flex items-center gap-stack px-page pt-page',
        `
          max-md:sticky max-md:top-0 max-md:z-10 max-md:bg-surface-base-normal max-md:py-stack
          max-md:shadow-[0_4px_8px_-4px_rgb(1_4_74_/_12%)]
        `,
        className,
      )}
      {...rest}
    >
      {/* The mobile nav opens from the page header row, immediately left of the title (Figma). Renders
          nothing when no `Sidebar.Provider` is mounted, so a `View` outside the shell is unaffected. */}
      <Sidebar.Trigger className="md:hidden" />
      {children}
    </div>
  )
}
namespace ViewHeader {
  export type Props = React.ComponentProps<'div'>
}

// The canonical view title: the `h4` visual size on the `h1` tag, per the Figma view frames — the
// document outline wants h1, the page hierarchy wants h4. The FO typescale already downsizes it
// (28/32 desktop, 22/28 mobile), so no responsive class is needed here.
function ViewTitle(props: ViewTitle.Props) {
  // `className` flows straight through: Heading merges it into its own variant classes.
  return <Heading variant="h4" level={1} data-slot="view-title" className="truncate" {...props} />
}
namespace ViewTitle {
  export type Props = React.ComponentProps<'h1'>
}

function ViewActions(props: ViewActions.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="view-actions"
      className={cn(
        'ml-auto flex shrink-0 items-center gap-inline',
        `
          max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:z-20 max-md:flex-col max-md:items-stretch max-md:border-t
          max-md:border-line-base-basic max-md:bg-[color-mix(in_oklab,var(--color-surface-base-normal)_10%,transparent)]
          max-md:p-50 max-md:pb-[calc(var(--spacing-50)+env(safe-area-inset-bottom))] max-md:backdrop-blur-md
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace ViewActions {
  export type Props = React.ComponentProps<'div'>
}

// Renders the `<main>` landmark: one View == one screen, so the body is the view's primary content
// region. Keeps content inside a landmark and gives skip links a target. `flex-1` makes it take the
// slack on a short page, which keeps the footer at the bottom; the scrolling itself belongs to
// `Layout.Body`.
function ViewBody(props: ViewBody.Props) {
  const { className, ...rest } = props
  return (
    <main
      data-slot="view-body"
      // max-md:pb clears the fixed bottom actions bar so the last content can scroll into view on mobile.
      className={cn(
        `
          flex flex-1 flex-col gap-section p-page
          max-md:pb-[7rem]
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace ViewBody {
  export type Props = React.ComponentProps<'main'>
}

function ViewFooter(props: ViewFooter.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="view-footer"
      className={cn(
        'flex items-center justify-end gap-inline border-t border-line-base-basic px-page py-stack',
        className,
      )}
      {...rest}
    />
  )
}
namespace ViewFooter {
  export type Props = React.ComponentProps<'div'>
}

const View = {
  Root: ViewRoot,
  Header: ViewHeader,
  Title: ViewTitle,
  Actions: ViewActions,
  Body: ViewBody,
  Footer: ViewFooter,
}

namespace View {
  export namespace Root {
    export type Props = ViewRoot.Props
  }
  export namespace Header {
    export type Props = ViewHeader.Props
  }
  export namespace Title {
    export type Props = ViewTitle.Props
  }
  export namespace Actions {
    export type Props = ViewActions.Props
  }
  export namespace Body {
    export type Props = ViewBody.Props
  }
  export namespace Footer {
    export type Props = ViewFooter.Props
  }
}

export { View }
